<template lang="pug">
	.paginate-wrapper
		div.info
			span.info-text Showing {{ offset + 1 }} - {{ lastRecord }} of <strong>{{ paginator.total }}</strong>
		div.paginate-side
			nav
				ul.pagination
					li.page-item(:class="{'disabled':paginator.page==1}")
						a.page-link(@click="prev()") &laquo;
					li.page-item(:class="{'active':paginator.page==1}")
						a.page-link(@click="setPage(1)") 1
					li.page-item.disabled(v-if="firstPage>2")
						a.page-link ...
					template(v-for="n in 3", :key="n")
						li.page-item(v-if="n <= lastPage", :class="{'active':paginator.page==n+firstPage-1}")
							a.page-link(@click="setPage(n-1+firstPage)") {{ n-1+firstPage }}
					li.page-item.disabled(v-if="firstPage+lastPage < paginator.numPages")
						a.page-link ...
					li.page-item(v-if="paginator.numPages>1", :class="{'active':paginator.page==paginator.numPages}")
						a.page-link(@click="setPage(paginator.numPages)") {{ paginator.numPages }}
					li.page-item(:class="{'disabled':paginator.page==paginator.numPages}")
						a.page-link(@click="next()") &raquo;
			div.page-size
				select.form-control(v-model.number="paginator.limit", @change="updatePageSize()")
					option(v-for="o in pageSizeOptions", :key="o", :value="o") {{ o }}
</template>

<script>
import { findPageSizeOwnerName, getStoredPageSize, resolvePageSizeStorageKey, setStoredPageSize } from "@/lib/pageSizePref";
import { useAuthStore } from "@/stores/auth";

export default {
	name: "Paginate",
	props: {
		paginator: {
			type: Object,
			required: true,
		},
	},
	emits: ["update-page-size"],
	data() {
		return {
			pageSizeOptions: [10, 20, 30, 40, 50, 100, 200, 500],
		};
	},
	computed: {
		// Storage key: route name, or `${route}:${OwnerName}` for Modal/Widget/Table/Panel parents.
		resolvedPageSizeKey() {
			return resolvePageSizeStorageKey({
				routeName: this.$route && this.$route.name,
				ownerName: findPageSizeOwnerName(this),
			});
		},
		currentUserId() {
			return useAuthStore().user?.id ?? null;
		},
		offset() {
			return (this.paginator.page - 1) * this.paginator.limit;
		},
		lastRecord() {
			return Math.min(this.paginator.total, this.offset + this.paginator.limit);
		},
		firstPage() {
			return Math.max(this.paginator.page - 1, 2);
		},
		lastPage() {
			return Math.min(3, this.paginator.numPages - this.firstPage);
		},
	},
	created() {
		this.restorePageSize();
	},
	methods: {
		restorePageSize() {
			if (!this.paginator) {
				return;
			}
			const userId = this.currentUserId;
			if (userId == null) {
				return;
			}
			const fallback = this.paginator.limit;
			const next = getStoredPageSize(
				userId,
				String(this.resolvedPageSizeKey),
				this.pageSizeOptions,
				fallback,
			);
			if (next !== this.paginator.limit) {
				this.paginator.limit = next;
			}
		},
		persistCurrentPageSize() {
			const userId = this.currentUserId;
			if (userId == null) {
				return;
			}
			setStoredPageSize(userId, String(this.resolvedPageSizeKey), this.paginator.limit);
		},
		updatePageSize() {
			this.persistCurrentPageSize();
			this.$emit("update-page-size");
		},
		setPage(p) {
			this.paginator.page = p;
			if (this.paginator.onPageChange) {
				this.paginator.onPageChange();
			}
		},
		next() {
			this.setPage(this.paginator.page + 1);
		},
		prev() {
			this.setPage(this.paginator.page - 1);
		},
	},
};
</script>
